import {HttpErrorResponse, HttpResponseBase} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {CargoErrorApiModel, FehlermeldungApiModel} from 'cargo-hub-ui-customer-api';
import {AutoSaveError} from '../../root-store/auto-save/auto-save.reducer';
import {NotificationHelper} from '../../root-store/notification/notification-helper';
import {notificationCreate} from '../../root-store/notification/notification.actions';
import {CdNotification} from '../../root-store/notification/notification.reducer';
import {LanguageService} from '../../services/language/language.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorService {
  static isConnectionError(error: HttpResponseBase): boolean {
    return error ? error.status === 0 : false;
  }

  static isCargoError(error: any): error is CargoErrorApiModel {
    return !!error && error instanceof Object && 'meldungen' in error;
  }

  constructor(private languageService: LanguageService) {}

  public getAutoSaveError(err: Error): AutoSaveError {
    if (err instanceof HttpErrorResponse) {
      if (ErrorService.isCargoError(err.error)) {
        return this.getAutoSaveErrorFromCargoError(err, err.error);
      }

      const statusText = err.statusText.replace(/^OK$/, '');
      const errorContent = `${err.status} ${statusText}`.trim();

      const i18nToken = ErrorService.isConnectionError(err)
        ? $localize`:@@HTTP_CONNECTION_ERROR:Bitte laden Sie die Seite neu. Falls erneut ein Fehler auftritt, wenden Sie sich bitte an den Support.\n\nNetzwerkfehler/Keine Verbindung`
        : $localize`:@@UNKNOWN_ERROR:Bitte laden Sie die Seite neu. Falls erneut ein Fehler auftritt, wenden Sie sich bitte an den Support.`;

      return {status: err.status ?? undefined, i18nToken, errorContent};
    }

    return {
      i18nToken: $localize`:@@UNKNOWN_ERROR:Bitte laden Sie die Seite neu. Falls erneut ein Fehler auftritt, wenden Sie sich bitte an den Support.`,
      errorContent: err.message
    };
  }

  private getAutoSaveErrorFromCargoError(err: HttpErrorResponse, cargoError: CargoErrorApiModel): AutoSaveError {
    const {status, statusText} = err;
    const validationMessages = this.getValidationMessages(cargoError);
    const errorMessages = this.getErrorMessages(cargoError);
    const errorContent = `${status} ${statusText.replace(/^OK$/, '')}`.trim();

    return {
      status,
      i18nToken: !!validationMessages
        ? $localize`:@@NOT_VALID_DATA:Ungültige Daten: ` +
          validationMessages +
          '\n\n' +
          $localize`:@@UNKNOWN_ERROR:Bitte laden Sie die Seite neu. Falls erneut ein Fehler auftritt, wenden Sie sich bitte an den Support.`
        : errorMessages,
      errorContent
    };
  }

  private getValidationMessages(cargoError: CargoErrorApiModel): string {
    return (
      cargoError.meldungen
        ?.filter(m => m.fehlermeldung.toUpperCase() === 'VALIDATION ERROR')
        .map(m => this.getReferenzenFeldFromFehlermeldungJoinedByComma(m))
        .filter(m => !!m)
        .join(', ') ?? ''
    );
  }

  private getErrorMessages(cargoError: CargoErrorApiModel): string {
    return cargoError.meldungen?.map(m => this.getErrorMessageInCorrectLanguage(m)).join(', ') ?? '';
  }

  getConnectionOrUnknownNotificationErrorAction(error: HttpResponseBase | any, title?: string): ReturnType<typeof notificationCreate> {
    const errorBody = error instanceof HttpErrorResponse ? error.error : null;
    // In case Internet connection trouble on client side
    if (ErrorService.isConnectionError(error)) {
      return notificationCreate({
        notification: NotificationHelper.createCDNotification({
          type: 'error',
          showInNotificationCenter: true,
          title: title ?? '',
          message: $localize`:@@HTTP_CONNECTION_ERROR:Bitte laden Sie die Seite neu. Falls erneut ein Fehler auftritt, wenden Sie sich bitte an den Support.\n\nNetzwerkfehler/Keine Verbindung`
        })
      });
    } else if (ErrorService.isCargoError(errorBody) && errorBody.meldungen?.length) {
      return notificationCreate({notification: this.resolveCargoError(errorBody, error.status, title)});
    } else {
      return notificationCreate({
        notification: NotificationHelper.createCDNotification({
          type: 'error',
          showInNotificationCenter: true,
          title: title ?? '',
          message: $localize`:@@UNKNOWN_ERROR:Bitte laden Sie die Seite neu. Falls erneut ein Fehler auftritt, wenden Sie sich bitte an den Support.`
        })
      });
    }
  }

  resolveCargoError(cargoError: CargoErrorApiModel, errorStatus: number, title?: string): CdNotification {
    return NotificationHelper.createCDNotification({
      type: 'error',
      showInNotificationCenter: true,
      title: title ?? '',
      message: cargoError.meldungen?.map(m => this.getErrorMessageInCorrectLanguage(m)).join(', ')
    });
  }

  getErrorMessageInCorrectLanguage(err: Pick<FehlermeldungApiModel, 'fehlermeldung' | 'fehlermeldungDe' | 'fehlermeldungFr' | 'fehlermeldungIt'>): string {
    switch (this.languageService.lang) {
      case 'de':
        return err.fehlermeldungDe ?? err.fehlermeldung;
      case 'fr':
        return err.fehlermeldungFr ?? err.fehlermeldung;
      case 'it':
        return err.fehlermeldungIt ?? err.fehlermeldung;
    }
  }

  getReferenzenFeldFromFehlermeldungJoinedByComma(meldung: FehlermeldungApiModel): string {
    return meldung.referenzen?.map(r => r.feld).join(', ') ?? '';
  }
}
